import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged
} from "firebase/auth";
import { 
  auth, 
  db 
} from "../../configs/firebase";
import { 
  doc, 
  getDoc, 
  setDoc,
  serverTimestamp 
} from "firebase/firestore";

const EAuthContext = createContext();
export const EAuthData = () => useContext(EAuthContext);

export const EAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employerVerificationStatus, setEmployerVerificationStatus] = useState(null);
  const [signupInProgress, setSignupInProgress] = useState(false);

  // Monitor auth state with employer verification check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔥 AUTH STATE CHANGED:', {
        hasUser: !!firebaseUser,
        uid: firebaseUser?.uid,
        email: firebaseUser?.email,
        signupInProgress: signupInProgress
      });

      if (firebaseUser) {
        try {
          console.log('👤 Fetching employer document for:', firebaseUser.uid);
          // Check if this is an employer
          const employerDoc = await getDoc(doc(db, 'employers', firebaseUser.uid));

          console.log('📄 Employer document exists:', employerDoc.exists());
          
          if (employerDoc.exists()) {
            const employerData = employerDoc.data();
            console.log('📊 Employer data:', {
              canLogin: employerData.canLogin,
              verificationStatus: employerData.verificationStatus,
              isActive: employerData.isActive,
              createdAt: employerData.createdAt
            });
            
            // Check if signup is in progress (new account created in last 30 seconds)
            const now = new Date();
            const createdAt = employerData.createdAt?.toDate();
            const isRecentSignup = createdAt && (now - createdAt) < 30000; // 30 seconds
            
            console.log('🔍 Employer verification check:', {
              canLogin: employerData.canLogin,
              verificationStatus: employerData.verificationStatus,
              isActive: employerData.isActive,
              isRecentSignup: isRecentSignup,
              signupInProgress: signupInProgress
            });
      
            // Don't interfere if signup just happened or is in progress
            if (isRecentSignup || signupInProgress) {
              console.log('✅ Allowing new signup to complete...');
              setUser({
                userd: firebaseUser,
                isAuthenticated: true,
                userType: 'employer',
                profile: employerData,
                verificationStatus: employerData.verificationStatus
              });
              setEmployerVerificationStatus(employerData.verificationStatus);
              
              // Clear the signup flag after a delay
              setTimeout(() => setSignupInProgress(false), 5000);
              
            } else if (employerData.canLogin && employerData.verificationStatus !== 'rejected') {
              // Check if employer is active
              if (employerData.isActive) {
                // Fully verified and active employer
                setUser({
                  userd: firebaseUser,
                  isAuthenticated: true,
                  userType: 'employer',
                  profile: employerData,
                  verificationStatus: employerData.verificationStatus,
                  isActive: employerData.isActive
                });
                setEmployerVerificationStatus(employerData.verificationStatus);
              } else {
                // not active - redirect to application submitted page
                setUser({
                  userd: firebaseUser,
                  isAuthenticated: true,
                  userType: 'employer',
                  profile: employerData,
                  verificationStatus: employerData.verificationStatus,
                  isActive: employerData.isActive,
                  redirectTo: 'application-submitted'
                });
                setEmployerVerificationStatus(employerData.verificationStatus);
              }
            } else {
              // User cannot login or is rejected
              console.log('❌ Employer account access denied, signing out...');
              await signOut(auth);
              setUser(null);
              setEmployerVerificationStatus(employerData.verificationStatus || 'pending');
            }
          } else {
            // No employer profile found
            console.log('❌ No employer document found');
            if (!signupInProgress) {
              console.log('⚠️ Signing out user - no signup in progress');
              await signOut(auth);
              setUser(null);
              setEmployerVerificationStatus(null);
            } else {
              console.log('✅ Keeping user signed in - signup in progress');
              // Keep user signed in during signup process - employer document will be created soon
              setUser({
                userd: firebaseUser,
                isAuthenticated: true,
                userType: 'employer',
                profile: null,
                verificationStatus: 'pending'
              });
              setEmployerVerificationStatus('pending');
            }
          }
        } catch (error) {
          console.error('❌ Error in auth state change:', error);
          console.error('Error fetching employer profile:', error);
          setUser({
            userd: firebaseUser,
            isAuthenticated: true,
            userType: 'employer',
            profile: null,
            verificationStatus: null
          });
          setEmployerVerificationStatus(null);
        }
      } else {
        console.log('👤 No Firebase user found');
        setUser(null);
        setEmployerVerificationStatus(null);
        setSignupInProgress(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [signupInProgress]);

  // Enhanced employer login with updated verification logic
  const login = async (values) => {
    try {
      const { email, password } = values;
      
      const cleanEmail = String(email || '').trim().toLowerCase();
      const cleanPassword = String(password || '').trim();

      console.log('🏢 Employer login attempt:', { cleanEmail });

      // Validation
      if (!cleanEmail || !cleanPassword) {
        throw new Error('Email and password are required');
      }

      if (!cleanEmail.includes('@')) {
        throw new Error('Invalid email format');
      }

      // Attempt Firebase login
      const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, cleanPassword);
      const firebaseUser = userCredential.user;

      // Check employer verification status
      const employerDoc = await getDoc(doc(db, 'employers', firebaseUser.uid));
      
      if (employerDoc.exists()) {
        const employerData = employerDoc.data();
        
        console.log('🔍 Login verification check:', {
          canLogin: employerData.canLogin,
          verificationStatus: employerData.verificationStatus,
          isActive: employerData.isActive
        });
        
        // Check if account is rejected
        if (employerData.verificationStatus === 'rejected') {
          await signOut(auth);
          throw new Error('Your account application was rejected. Please contact support for more information.');
        }
        
        // Check if user cannot login
        if (!employerData.canLogin) {
          await signOut(auth);
          throw new Error('Your account is pending verification. Please wait for approval.');
        }
        
        // At this point: verificationStatus is NOT rejected AND canLogin is true
        // Check if account is not active - redirect to ApplicationSubmitted
        if (!employerData.isActive) {
          // Don't sign out, but throw specific error to trigger navigation
          throw new Error('application-submitted');
        }
        
        // All conditions met: can login, not rejected, and active
        console.log('✅ Employer login successful and verified');
        return firebaseUser;
        
      } else {
        await signOut(auth);
        throw new Error('Employer account not found. Please register first.');
      }
    } catch (error) {
      console.error('❌ Employer login error:', error);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setEmployerVerificationStatus(null);
      setSignupInProgress(false);
      console.log('✅ Employer logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    }
  };

  // Update employer profile
  const updateEmployerProfile = async (uid, updateData) => {
    try {
      const employerRef = doc(db, 'employers', uid);
      await setDoc(employerRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      console.log('✅ Employer profile updated successfully');
      return true;
    } catch (error) {
      console.error('❌ Profile update error:', error);
      throw error;
    }
  };

  // Set signup in progress flag (call this from signup component)
  const setSignupInProgressFlag = (inProgress) => {
    setSignupInProgress(inProgress);
  };

  // Check employer verification status
  const checkEmployerVerificationStatus = async (uid) => {
    try {
      const employerDoc = await getDoc(doc(db, 'employers', uid));
      if (employerDoc.exists()) {
        return employerDoc.data().verificationStatus;
      }
      return null;
    } catch (error) {
      console.error('Error checking verification status:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    employerVerificationStatus,
    
    // Authentication methods
    login,
    logout,
    
    // Utility methods
    updateEmployerProfile,
    checkEmployerVerificationStatus,
    setSignupInProgressFlag,
    
    // User type helpers
    isEmployer: () => user?.userType === 'employer',
    isVerifiedEmployer: () => user?.userType === 'employer' && user?.verificationStatus === 'approved',
    isActiveEmployer: () => user?.userType === 'employer' && user?.isActive === true
  };

  return (
    <EAuthContext.Provider value={value}>
      {!loading && children}
    </EAuthContext.Provider>
  );
};